import { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, Tag } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GROUPS = [
  { key: 'Cultural', label: 'Cultural', color: 'border-purple-200 bg-purple-50', accent: 'bg-purple-600 hover:bg-purple-700', textColor: 'text-purple-700' },
  { key: 'Sport', label: 'Sport', color: 'border-emerald-200 bg-emerald-50', accent: 'bg-emerald-600 hover:bg-emerald-700', textColor: 'text-emerald-700' },
  { key: 'Altele', label: 'Altele', color: 'border-amber-200 bg-amber-50', accent: 'bg-amber-600 hover:bg-amber-700', textColor: 'text-amber-700' },
];

export default function EventCategoriesManager({ secureFetch }) {
  const [categories, setCategories] = useState({ Cultural: [], Sport: [], Altele: [] });
  const [newNames, setNewNames] = useState({ Cultural: '', Sport: '', Altele: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/event-categories`);
      if (res.ok) setCategories(await res.json());
    } catch (e) {
      console.error(e);
      toast.error('Eroare la încărcarea categoriilor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (group) => {
    const name = newNames[group].trim();
    if (!name) {
      toast.error('Introdu un nume');
      return;
    }
    setSaving(prev => ({ ...prev, [group]: true }));
    try {
      const res = await secureFetch(`${API_URL}/api/admin/event-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group, name }),
      });
      if (res.ok) {
        toast.success(`"${name}" adăugat în ${group}`);
        setNewNames(prev => ({ ...prev, [group]: '' }));
        await load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Eroare la salvare');
      }
    } catch (e) {
      toast.error('Eroare de rețea');
    } finally {
      setSaving(prev => ({ ...prev, [group]: false }));
    }
  };

  const handleDelete = async (group, cat) => {
    if (!window.confirm(`Ștergi "${cat.name}" din ${group}?`)) return;
    try {
      const res = await secureFetch(`${API_URL}/api/admin/event-categories/${cat.id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        toast.success('Șters');
        await load();
      } else {
        toast.error('Eroare la ștergere');
      }
    } catch (e) {
      toast.error('Eroare de rețea');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="event-cats-loading">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="event-categories-manager">
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-navy/10 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <Tag className="w-5 h-5 text-gold" />
          <h2 className="text-xl font-bold text-navy">Categorii Evenimente</h2>
        </div>
        <p className="text-navy/60 text-sm">
          Gestionează subcategoriile pentru fiecare grup. Acestea apar la crearea unui eveniment și ca filtre pe pagina <code className="px-1 bg-navy/5 rounded">/concerte/evenimente</code>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {GROUPS.map(g => {
          const items = categories[g.key] || [];
          return (
            <div key={g.key} className={`rounded-2xl border p-5 ${g.color}`} data-testid={`event-cat-group-${g.key.toLowerCase()}`}>
              <h3 className={`font-bold text-lg ${g.textColor} mb-1`}>{g.label}</h3>
              <p className="text-xs text-slate-600 mb-3">{items.length} {items.length === 1 ? 'subcategorie' : 'subcategorii'}</p>

              {/* Add form */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newNames[g.key]}
                  onChange={e => setNewNames(prev => ({ ...prev, [g.key]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(g.key); } }}
                  placeholder="ex: Fotbal"
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm bg-white"
                  data-testid={`input-${g.key.toLowerCase()}`}
                />
                <Button
                  onClick={() => handleAdd(g.key)}
                  disabled={saving[g.key]}
                  className={`${g.accent} text-white`}
                  data-testid={`add-${g.key.toLowerCase()}`}
                >
                  {saving[g.key] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>

              {/* List */}
              <div className="space-y-2">
                {items.length === 0 ? (
                  <p className="text-sm text-slate-500 italic px-2 py-3 text-center">Nicio subcategorie</p>
                ) : (
                  items.map(c => (
                    <div key={c.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-200" data-testid={`cat-item-${c.id}`}>
                      <span className="text-sm text-slate-700">{c.name}</span>
                      <button
                        onClick={() => handleDelete(g.key, c)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        data-testid={`delete-cat-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
